import React from 'react';
import Decimal from 'decimal.js';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Text, Box } from 'roo-ui/components';
import { display, fontSize, space, lineHeight, style, color, fontWeight, themeGet } from 'styled-system';
import { FormatNumber } from 'components/formatters';

const CURRENCY_SYMBOLS = {
  AUD: '$',
};

const resolveCurrencySymbol = (currency) => CURRENCY_SYMBOLS[currency];

const textDecoration = style({
  prop: 'textDecoration',
  cssProperty: 'textDecoration',
});

const Wrapper = styled(Box)`
  ${lineHeight};
  ${space};
  ${display}
  ${fontSize};
  ${color};
  ${fontWeight};
  display: inline-flex;
  justify-content: flex-end;
  align-items: baseline;
  flex-flow: ${(props) => (props.reverse ? 'row-reverse' : 'row')};
`;

const Amount = styled.span`
  ${textDecoration};
  ${lineHeight};
  ${fontWeight};
`;

Amount.displayName = 'Amount';

const CurrencyText = styled.span`
  ${space};
  ${lineHeight};
  text-transform: ${(props) => (props.isPoints ? 'lowercase' : 'uppercase')};
  margin-left: ${(props) => (props.reverse ? themeGet('space.1') : 0)};
`;

CurrencyText.displayName = 'CurrencyText';

const Currency = ({
  amount,
  currency,
  roundToCeiling,
  fontSize,
  hideCurrency,
  alignCurrency,
  lineHeight,
  fontWeight,
  textDecoration,
  ...rest
}) => {
  if (amount === null || amount === undefined) return null;

  const currencySymbol = resolveCurrencySymbol(currency);
  const number = roundToCeiling ? new Decimal(amount).ceil() : new Decimal(amount);
  const decimalPlaces = currency === 'PTS' || roundToCeiling ? 0 : 2;
  const isPoints = currency === 'PTS';
  const reverse = alignCurrency !== 'right';

  return (
    <Wrapper fontSize={fontSize} lineHeight={lineHeight} fontWeight={fontWeight} isPoints={isPoints} reverse={reverse} {...rest}>
      <Box>
        {currencySymbol && (
          <Text data-testid="currency-symbol" fontSize={fontSize} lineHeight={lineHeight} fontWeight={fontWeight}>
            {currencySymbol}
          </Text>
        )}
        <Amount lineHeight={lineHeight} textDecoration={textDecoration} data-testid="amount">
          <FormatNumber number={number} decimalPlaces={decimalPlaces} />
        </Amount>
      </Box>
      {!hideCurrency && (
        <Box mr={reverse ? 1 : 0} ml={reverse ? 0 : 1}>
          <CurrencyText lineHeight={lineHeight} isPoints={isPoints} reverse={reverse} data-testid="currency-text">
            {currency}
          </CurrencyText>
        </Box>
      )}
    </Wrapper>
  );
};

Currency.propTypes = {
  amount: PropTypes.oneOfType([PropTypes.instanceOf(Decimal), PropTypes.number, PropTypes.string]),
  currency: PropTypes.oneOf(['AUD', 'PTS']),
  roundToCeiling: PropTypes.bool,
  fontSize: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ]),
  hideCurrency: PropTypes.bool,
  'data-testid': PropTypes.string,
  lineHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  textDecoration: PropTypes.string,
  fontWeight: PropTypes.string,
  alignCurrency: PropTypes.oneOf(['left', 'right']),
};

Currency.defaultProps = {
  amount: null,
  currency: null,
  'data-testid': 'currency',
  lineHeight: 'tight',
  fontSize: 'base',
  hideCurrency: false,
  roundToCeiling: false,
  textDecoration: 'none',
  fontWeight: 'normal',
  alignCurrency: 'right',
};

export default Currency;
